import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  BarChart3,
  TrendingUp,
  Calendar,
  DollarSign,
  Clock,
  ChevronDown,
  ChevronUp,
  Printer,
  Download,
  Trash2
} from 'lucide-react';
import { DocketPropType } from '../../../types/propTypes';
import './analyticsPanel.css';

const AnalyticsPanel = ({
  selectedCases = [],
  onPrint,
  onExport,
  onClearAll,
  searchQuery,
  filters
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  // Helper functions
  // const parseLocalDate = (dateString) => {
  //   if (!dateString) return null;
  //   const [year, month, day] = dateString.split('-').map(Number);
  //   return new Date(year, month - 1, day);
  // };

  // const formatDate = (dateString) => {
  //   if (!dateString) return 'N/A';
  //   const date = parseLocalDate(dateString);
  //   return date ? date.toLocaleDateString() : 'N/A';
  // };

  const getTotalSentence = (counts) => {
    if (!counts || !counts.length) return 0;
    return counts.reduce((total, count) => {
      if (!count.sentence) return total;
      const { value, unit } = count.sentence;
      if (unit === 'months') return total + value;
      if (unit === 'years') return total + value * 12;
      if (unit === 'days') return total + value / 30.44;
      return total;
    }, 0);
  };

  const getTotalFines = (counts) => {
    if (!counts || !counts.length) return 0;
    return counts.reduce((total, count) => {
      if (!count.fine) return total;
      return total + count.fine.value;
    }, 0);
  };

  const getTotalConfinement = (counts) => {
    if (!counts || !counts.length) return 0;
    return counts.reduce((total, count) => {
      if (!count.confinement) return total;
      const { value, unit } = count.confinement;
      if (unit === 'months') return total + value;
      if (unit === 'years') return total + value * 12;
      if (unit === 'days') return total + value / 30.44;
      return total;
    }, 0);
  };

  const getTotalProbation = (counts) => {
    if (!counts || !counts.length) return 0;
    return counts.reduce((total, count) => {
      if (!count.probation) return total;
      const { value, unit } = count.probation;
      if (unit === 'months') return total + value;
      if (unit === 'years') return total + value * 12;
      if (unit === 'days') return total + value / 30.44;
      return total;
    }, 0);
  };

  // Helper function to get primary charge
  const getPrimaryCharge = (counts) => {
    if (!counts || counts.length === 0) return 'Unknown Charge';

    // Find the most serious charge (with highest sentence)
    let primaryCharge = counts[0];
    let maxSentence = getTotalSentence([counts[0]]);

    for (const count of counts) {
      const sentence = getTotalSentence([count]);
      if (sentence > maxSentence) {
        maxSentence = sentence;
        primaryCharge = count;
      }
    }

    return primaryCharge.chargeDescription || 'Unknown Charge';
  };

  // Helper function to get disposition summary based on actual data
  const getDispositionSummary = (caseData) => {
    if (!caseData) return 'Unknown';

    // Use case-level dismissal status
    if (caseData.isDismissed) return 'Dismissed';

    // Use sentencing types to determine outcome
    if (caseData.sentencingTypes && caseData.sentencingTypes.length > 0) {
      // If it has sentencing types, it's likely resolved
      if (caseData.sentencingTypes.includes('Dismissed')) return 'Dismissed';
      if (caseData.sentencingTypes.includes('Diversion')) return 'Diversion';
      if (caseData.sentencingTypes.includes('Confinement'))
        return 'Confinement';
      if (caseData.sentencingTypes.includes('Probation')) return 'Probation';
      if (caseData.sentencingTypes.includes('Fine')) return 'Fine';

      // If it has any sentencing type, it's resolved
      return 'Resolved';
    }

    // Check if case has sentences (indicating it's resolved)
    if (caseData.counts && caseData.counts.length > 0) {
      const hasSentence = caseData.counts.some(
        (count) =>
          count.sentence ||
          (count.sentencing &&
            (count.sentencing.confinements?.length > 0 ||
              count.sentencing.probations?.length > 0))
      );

      if (hasSentence) return 'Resolved';
    }

    // Check case status
    if (caseData.caseStatus) {
      const status = caseData.caseStatus.toLowerCase();
      if (status.includes('closed') || status.includes('disposed'))
        return 'Closed';
      if (status.includes('active') || status.includes('open')) return 'Active';
    }

    // Default based on whether case has a close date
    if (caseData.closeDate) return 'Closed';

    return 'Active';
  };

  // Helper function to get detailed outcome information for a specific charge
  const getChargeOutcomeDetails = (chargeData) => {
    if (!chargeData) return 'No outcome data';

    // Check for dismissal
    if (chargeData.isDismissed) return 'Dismissed';

    // Check sentencing types
    if (chargeData.sentencingTypes && chargeData.sentencingTypes.length > 0) {
      const types = chargeData.sentencingTypes;
      if (types.includes('Dismissed')) return 'Dismissed';
      if (types.includes('Diversion')) return 'Diversion';
      if (types.includes('Probation')) return 'Probation';
      if (types.includes('Confinement')) return 'Confinement';
      if (types.includes('Fine')) return 'Fine';
      return 'Resolved';
    }

    // Check for actual sentencing data
    if (chargeData.sentencing) {
      const { confinements, probations, fines } = chargeData.sentencing;
      if (confinements && confinements.length > 0) return 'Confinement';
      if (probations && probations.length > 0) return 'Probation';
      if (fines && fines.length > 0) return 'Fine';
    }

    // Check for sentence data
    if (chargeData.sentence) {
      return 'Sentenced';
    }

    return 'No outcome data';
  };

  // Calculate charge-specific analytics
  const chargeBreakdown = {};
  const judgeAnalytics = {};
  const outcomeAnalysis = {
    totalCases: selectedCases.length,
    dispositions: {},
    caseStatuses: {},
    averageDuration: 0
  };

  selectedCases.forEach((caseData) => {
    const primaryCharge = getPrimaryCharge(caseData.counts);
    const disposition = getDispositionSummary(caseData);
    const caseStatus = caseData.caseStatus || 'Unknown';

    // Get the sentence for the primary charge specifically
    const primaryChargeData = caseData.counts.find(
      (count) => count.chargeDescription === primaryCharge
    );
    const chargeSpecificSentence = primaryChargeData
      ? getTotalSentence([primaryChargeData])
      : 0;
    const chargeSpecificFines = primaryChargeData
      ? getTotalFines([primaryChargeData])
      : 0;
    const chargeSpecificConfinement = primaryChargeData
      ? getTotalConfinement([primaryChargeData])
      : 0;
    const chargeSpecificProbation = primaryChargeData
      ? getTotalProbation([primaryChargeData])
      : 0;

    // Charge breakdown
    if (!chargeBreakdown[primaryCharge]) {
      chargeBreakdown[primaryCharge] = {
        count: 0,
        sentences: [],
        caseSentences: [], // Store case number with sentence and outcome
        averageSentence: 0,
        totalFines: 0,
        casesWithConfinement: 0,
        casesWithProbation: 0,
        dispositions: {}
      };
    }

    chargeBreakdown[primaryCharge].count++;
    chargeBreakdown[primaryCharge].sentences.push(chargeSpecificSentence);

    // Get detailed outcome for this specific charge
    const chargeOutcome = primaryChargeData
      ? getChargeOutcomeDetails(primaryChargeData)
      : 'No outcome data';

    chargeBreakdown[primaryCharge].caseSentences.push({
      caseNumber: caseData.caseNumber,
      sentence: chargeSpecificSentence,
      outcome: chargeOutcome
    });
    chargeBreakdown[primaryCharge].totalFines += chargeSpecificFines;
    if (chargeSpecificConfinement > 0)
      chargeBreakdown[primaryCharge].casesWithConfinement++;
    if (chargeSpecificProbation > 0)
      chargeBreakdown[primaryCharge].casesWithProbation++;

    // Track dispositions for this charge
    if (!chargeBreakdown[primaryCharge].dispositions[disposition]) {
      chargeBreakdown[primaryCharge].dispositions[disposition] = 0;
    }
    chargeBreakdown[primaryCharge].dispositions[disposition]++;

    // Judge analytics
    if (caseData.judges && caseData.judges.length > 0) {
      caseData.judges.forEach((judge) => {
        if (judge.firstName && judge.firstName !== 'Department') {
          const judgeName = `${judge.firstName} ${judge.lastName}`;

          if (!judgeAnalytics[judgeName]) {
            judgeAnalytics[judgeName] = {
              totalCases: 0,
              charges: {},
              averageSentence: 0,
              sentences: []
            };
          }

          judgeAnalytics[judgeName].totalCases++;
          judgeAnalytics[judgeName].sentences.push(chargeSpecificSentence);

          // Track charges for this judge
          if (!judgeAnalytics[judgeName].charges[primaryCharge]) {
            judgeAnalytics[judgeName].charges[primaryCharge] = {
              count: 0,
              sentences: [],
              caseOutcomes: [] // Store case number with sentence and outcome
            };
          }
          judgeAnalytics[judgeName].charges[primaryCharge].count++;
          judgeAnalytics[judgeName].charges[primaryCharge].sentences.push(
            chargeSpecificSentence
          );
          judgeAnalytics[judgeName].charges[primaryCharge].caseOutcomes.push({
            caseNumber: caseData.caseNumber,
            sentence: chargeSpecificSentence,
            outcome: chargeOutcome
          });
        }
      });
    }

    // Outcome analysis
    if (!outcomeAnalysis.dispositions[disposition]) {
      outcomeAnalysis.dispositions[disposition] = 0;
    }
    outcomeAnalysis.dispositions[disposition]++;

    if (!outcomeAnalysis.caseStatuses[caseStatus]) {
      outcomeAnalysis.caseStatuses[caseStatus] = 0;
    }
    outcomeAnalysis.caseStatuses[caseStatus]++;

    outcomeAnalysis.averageDuration += caseData.durationDays || 0;
  });

  // Calculate averages
  Object.keys(chargeBreakdown).forEach((charge) => {
    const data = chargeBreakdown[charge];
    data.averageSentence =
      data.sentences.reduce((sum, s) => sum + s, 0) / data.sentences.length;
  });

  Object.keys(judgeAnalytics).forEach((judge) => {
    const data = judgeAnalytics[judge];
    data.averageSentence =
      data.sentences.reduce((sum, s) => sum + s, 0) / data.sentences.length;

    // Calculate averages for each charge type
    Object.keys(data.charges).forEach((charge) => {
      const chargeData = data.charges[charge];
      chargeData.averageSentence =
        chargeData.sentences.reduce((sum, s) => sum + s, 0) /
        chargeData.sentences.length;
    });
  });

  outcomeAnalysis.averageDuration =
    outcomeAnalysis.averageDuration / selectedCases.length;

  const formatMonths = (months) => {
    if (months === 0) return 'No sentence';
    if (months < 12) return `${months.toFixed(1)} months`;
    return `${(months / 12).toFixed(1)} years`;
  };

  // Always show the panel, but with different content based on selection

  return (
    <div className="analytics-panel bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
      {/* Header - Always Visible */}
      <div
        className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-gray-200 cursor-pointer hover:from-blue-100 hover:to-indigo-100 transition-colors"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <BarChart3 className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Case Analytics & Insights
              </h3>
              {selectedCases.length === 0 ? (
                <p className="text-sm text-gray-600">
                  Click the selection button on any case card to see detailed
                  analytics
                </p>
              ) : (
                <p className="text-sm text-gray-600">
                  {selectedCases.length} case
                  {selectedCases.length !== 1 ? 's' : ''} selected • Click to
                  view insights
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {selectedCases.length > 0 && (
              <>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onClearAll();
                  }}
                  className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-red-600 bg-red-50 hover:bg-red-100 border border-red-200 rounded-lg transition-colors"
                  title="Clear all selections"
                >
                  <Trash2 className="w-4 h-4" />
                  <span className="hidden sm:inline">Clear All</span>
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onPrint(selectedCases);
                  }}
                  className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors shadow-sm"
                >
                  <Printer className="w-4 h-4" />
                  Print
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onExport(selectedCases);
                  }}
                  className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors shadow-sm"
                >
                  <Download className="w-4 h-4" />
                  Export
                </button>
              </>
            )}
            <div className="p-1">
              {isExpanded ? (
                <ChevronUp className="w-5 h-5 text-gray-500" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-500" />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Expandable Content */}
      {isExpanded && (
        <div className="analytics-content">
          <div className="p-6 space-y-6">
            {selectedCases.length === 0 ? (
              /* Empty State - Show what analytics will provide */
              <div className="text-center py-8">
                <div className="max-w-md mx-auto">
                  <div className="p-4 bg-blue-50 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                    <BarChart3 className="w-8 h-8 text-blue-600" />
                  </div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">
                    Unlock Powerful Case Insights
                  </h4>
                  <p className="text-gray-600 mb-6">
                    Select cases from your search results to get detailed
                    analytics including:
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-left">
                    <div className="flex items-start gap-3">
                      <div className="p-1 bg-green-100 rounded">
                        <TrendingUp className="w-4 h-4 text-green-600" />
                      </div>
                      <div>
                        <div className="font-medium text-gray-900">
                          Charge Analysis
                        </div>
                        <div className="text-gray-600">
                          Average sentences by charge type
                        </div>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="p-1 bg-purple-100 rounded">
                        <Calendar className="w-4 h-4 text-purple-600" />
                      </div>
                      <div>
                        <div className="font-medium text-gray-900">
                          Judge Analytics
                        </div>
                        <div className="text-gray-600">
                          Sentencing patterns by judge
                        </div>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="p-1 bg-orange-100 rounded">
                        <Clock className="w-4 h-4 text-orange-600" />
                      </div>
                      <div>
                        <div className="font-medium text-gray-900">
                          Outcome Analysis
                        </div>
                        <div className="text-gray-600">
                          Case dispositions and statuses
                        </div>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="p-1 bg-blue-100 rounded">
                        <DollarSign className="w-4 h-4 text-blue-600" />
                      </div>
                      <div>
                        <div className="font-medium text-gray-900">
                          Financial Impact
                        </div>
                        <div className="text-gray-600">
                          Fines and cost analysis
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-700">
                      <strong>How to get started:</strong> Look for the{' '}
                      <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-white border border-blue-300 text-blue-600 text-xs font-bold mx-1">
                        +
                      </span>{' '}
                      plus button on any case card in your search results and
                      click it to begin building your analytics report.
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              /* Analytics Content */
              <>
                {/* Charge-Specific Breakdown */}
                <div className="space-y-4">
                  <h4 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-blue-600" />
                    Charge-Specific Analysis
                  </h4>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {Object.entries(chargeBreakdown)
                      .sort(([, a], [, b]) => b.count - a.count)
                      .map(([charge, data]) => (
                        <div
                          key={charge}
                          className="bg-white border border-gray-200 rounded-lg p-4"
                        >
                          <h5 className="font-semibold text-gray-900 mb-3 text-sm">
                            {charge}
                          </h5>

                          <div className="space-y-2 text-xs">
                            <div className="flex justify-between">
                              <span className="text-gray-600">Cases:</span>
                              <span className="font-medium">{data.count}</span>
                            </div>

                            <div className="flex justify-between">
                              <span className="text-gray-600">
                                Avg Sentence:
                              </span>
                              <span className="font-medium">
                                {formatMonths(data.averageSentence)}
                              </span>
                            </div>
                          </div>

                          {/* Individual Sentences */}
                          <div className="mt-3 pt-3 border-t border-gray-100">
                            <p className="text-xs font-medium text-gray-700 mb-2">
                              Individual Outcomes:
                            </p>
                            <div className="space-y-1">
                              {data.caseSentences
                                .sort((a, b) => b.sentence - a.sentence)
                                .slice(0, 5)
                                .map((caseSentence, idx) => (
                                  <div
                                    key={idx}
                                    className="flex justify-between items-center text-xs"
                                  >
                                    <span className="text-gray-600">
                                      {caseSentence.caseNumber}:
                                    </span>
                                    <div className="text-right">
                                      <div className="font-medium">
                                        {caseSentence.sentence > 0
                                          ? formatMonths(caseSentence.sentence)
                                          : caseSentence.outcome}
                                      </div>
                                      {caseSentence.sentence > 0 &&
                                        caseSentence.outcome !==
                                          'Sentenced' && (
                                          <div className="text-gray-500 text-xs">
                                            ({caseSentence.outcome})
                                          </div>
                                        )}
                                    </div>
                                  </div>
                                ))}
                              {data.caseSentences.length > 5 && (
                                <div className="text-xs text-gray-500 italic">
                                  ... and {data.caseSentences.length - 5} more
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Dispositions */}
                          {Object.keys(data.dispositions).length > 0 && (
                            <div className="mt-3 pt-3 border-t border-gray-100">
                              <p className="text-xs font-medium text-gray-700 mb-2">
                                Outcomes:
                              </p>
                              <div className="flex flex-wrap gap-1">
                                {Object.entries(data.dispositions)
                                  .sort(([, a], [, b]) => b - a)
                                  .map(([disposition, count]) => (
                                    <span
                                      key={disposition}
                                      className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs"
                                    >
                                      {disposition} ({count})
                                    </span>
                                  ))}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                  </div>
                </div>

                {/* Judge Analytics */}
                {Object.keys(judgeAnalytics).length > 0 && (
                  <div className="bg-gray-50 rounded-lg p-6">
                    <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <TrendingUp className="w-5 h-5 text-green-600" />
                      Judge-Specific Analytics
                    </h4>
                    <div className="space-y-4">
                      {Object.entries(judgeAnalytics)
                        .sort(([, a], [, b]) => b.totalCases - a.totalCases)
                        .map(([judge, data]) => (
                          <div
                            key={judge}
                            className="bg-white border border-gray-200 rounded-lg p-4"
                          >
                            <h5 className="font-semibold text-gray-900 mb-3 text-sm">
                              {judge}
                            </h5>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
                              <div>
                                <span className="text-gray-600">
                                  Total Cases:
                                </span>
                                <span className="font-medium ml-2">
                                  {data.totalCases}
                                </span>
                              </div>
                              <div>
                                <span className="text-gray-600">
                                  Avg Sentence:
                                </span>
                                <span className="font-medium ml-2">
                                  {formatMonths(data.averageSentence)}
                                </span>
                              </div>
                              <div>
                                <span className="text-gray-600">
                                  Charge Types:
                                </span>
                                <span className="font-medium ml-2">
                                  {Object.keys(data.charges).length}
                                </span>
                              </div>
                            </div>

                            {/* Charge-specific data for this judge */}
                            {Object.keys(data.charges).length > 0 && (
                              <div className="mt-3 pt-3 border-t border-gray-100">
                                <p className="text-xs font-medium text-gray-700 mb-2">
                                  By Charge Type:
                                </p>
                                <div className="space-y-2">
                                  {Object.entries(data.charges)
                                    .sort(([, a], [, b]) => b.count - a.count)
                                    .slice(0, 3)
                                    .map(([charge, chargeData]) => (
                                      <div key={charge} className="space-y-1">
                                        <div className="flex justify-between text-xs">
                                          <span className="text-gray-600 truncate flex-1 mr-2">
                                            {charge.length > 30
                                              ? charge.substring(0, 30) + '...'
                                              : charge}
                                          </span>
                                          <span className="font-medium">
                                            {chargeData.count} cases, avg{' '}
                                            {formatMonths(
                                              chargeData.averageSentence
                                            )}
                                          </span>
                                        </div>
                                        {/* Show individual case outcomes for this judge/charge combination */}
                                        <div className="ml-2 space-y-1">
                                          {chargeData.caseOutcomes
                                            .slice(0, 2)
                                            .map((caseOutcome, idx) => (
                                              <div
                                                key={idx}
                                                className="flex justify-between text-xs text-gray-500"
                                              >
                                                <span>
                                                  {caseOutcome.caseNumber}:
                                                </span>
                                                <span>
                                                  {caseOutcome.sentence > 0
                                                    ? formatMonths(
                                                        caseOutcome.sentence
                                                      )
                                                    : caseOutcome.outcome}
                                                </span>
                                              </div>
                                            ))}
                                          {chargeData.caseOutcomes.length >
                                            2 && (
                                            <div className="text-xs text-gray-400 italic">
                                              ... and{' '}
                                              {chargeData.caseOutcomes.length -
                                                2}{' '}
                                              more
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    ))}
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                    </div>
                  </div>
                )}

                {/* Outcome Analysis */}
                <div className="bg-gray-50 rounded-lg p-6">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-purple-600" />
                    Case Outcome Analysis
                  </h4>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Case Outcomes */}
                    <div>
                      <h5 className="font-medium text-gray-900 mb-3 text-sm">
                        Case Outcomes
                      </h5>
                      <div className="space-y-2">
                        {Object.entries(outcomeAnalysis.dispositions)
                          .sort(([, a], [, b]) => b - a)
                          .map(([outcome, count]) => {
                            const percentage =
                              (count / outcomeAnalysis.totalCases) * 100;

                            // Add helpful descriptions for outcomes
                            const getOutcomeDescription = (outcome) => {
                              switch (outcome) {
                                case 'Dismissed':
                                  return 'Case was dismissed';
                                case 'Diversion':
                                  return 'Defendant entered diversion program';
                                case 'Confinement':
                                  return 'Defendant received jail/prison time';
                                case 'Probation':
                                  return 'Defendant received probation';
                                case 'Fine':
                                  return 'Defendant received fine only';
                                case 'Resolved':
                                  return 'Case resolved with sentencing';
                                case 'Closed':
                                  return 'Case closed without sentencing';
                                case 'Active':
                                  return 'Case still active/ongoing';
                                default:
                                  return '';
                              }
                            };

                            return (
                              <div
                                key={outcome}
                                className="flex items-center justify-between text-sm"
                              >
                                <div>
                                  <span className="text-gray-700 font-medium">
                                    {outcome}
                                  </span>
                                  {getOutcomeDescription(outcome) && (
                                    <div className="text-xs text-gray-500">
                                      {getOutcomeDescription(outcome)}
                                    </div>
                                  )}
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className="text-gray-600">{count}</span>
                                  <span className="text-xs text-gray-500">
                                    ({percentage.toFixed(1)}%)
                                  </span>
                                </div>
                              </div>
                            );
                          })}
                      </div>
                    </div>

                    {/* Case Statuses */}
                    <div>
                      <h5 className="font-medium text-gray-900 mb-3 text-sm">
                        Case Status
                      </h5>
                      <div className="space-y-2">
                        {Object.entries(outcomeAnalysis.caseStatuses)
                          .sort(([, a], [, b]) => b - a)
                          .map(([status, count]) => {
                            const percentage =
                              (count / outcomeAnalysis.totalCases) * 100;
                            return (
                              <div
                                key={status}
                                className="flex items-center justify-between text-sm"
                              >
                                <span className="text-gray-700">{status}</span>
                                <div className="flex items-center gap-2">
                                  <span className="text-gray-600">{count}</span>
                                  <span className="text-xs text-gray-500">
                                    ({percentage.toFixed(1)}%)
                                  </span>
                                </div>
                              </div>
                            );
                          })}
                      </div>
                    </div>
                  </div>

                  {/* Average Duration */}
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-700">
                        Average Case Duration:
                      </span>
                      <span className="font-medium text-gray-900">
                        {outcomeAnalysis.averageDuration > 0
                          ? `${Math.round(outcomeAnalysis.averageDuration)} days`
                          : 'N/A'}
                      </span>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

AnalyticsPanel.propTypes = {
  selectedCases: PropTypes.arrayOf(DocketPropType).isRequired,
  onPrint: PropTypes.func.isRequired,
  onExport: PropTypes.func.isRequired,
  onClearAll: PropTypes.func.isRequired,
  searchQuery: PropTypes.string,
  filters: PropTypes.object
};

export default AnalyticsPanel;
